/** 日付ユーティリティ。タイムゾーンは端末ローカル固定（仕様書 §20）。 */

import type { IsoLocal, IsoUtc, MonthKey } from '../domain/types';

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/** Date → "2026-08-26T14:30:00+09:00"（端末ローカル時刻＋オフセット） */
export function toIsoLocal(d: Date): IsoLocal {
  const offMin = -d.getTimezoneOffset();
  const sign = offMin >= 0 ? '+' : '-';
  const abs = Math.abs(offMin);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

export function nowIsoUtc(): IsoUtc {
  return new Date().toISOString();
}

/** "2026-08-26T14:30:00+09:00" → Date */
export function parseIso(s: string): Date {
  return new Date(s);
}

/** Date → "2026-08-26"（ローカル日付） */
export function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Date → "2026-08"。記事の保存先フォルダを決める（§4.3）。 */
export function toMonthKey(d: Date): MonthKey {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

/** IsoLocal 文字列から直接 monthKey を得る（Dateを経由せず、記録された地方時をそのまま使う）。 */
export function monthKeyOfIsoLocal(iso: IsoLocal): MonthKey {
  return iso.slice(0, 7);
}

/** IsoLocal 文字列から直接 dateKey を得る。 */
export function dateKeyOfIsoLocal(iso: IsoLocal): string {
  return iso.slice(0, 10);
}

export function dateKeyToDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function monthKeyToDate(key: MonthKey): Date {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function addMonths(d: Date, n: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth() + n, 1);
  return r;
}

/** 週の開始は月曜（§12.4）。 */
export function startOfWeek(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (r.getDay() + 6) % 7; // 月=0 … 日=6
  r.setDate(r.getDate() - dow);
  return r;
}

/** ISO-8601 週番号。"2026-W35" 形式のキーを返す。 */
export function toWeekKey(d: Date): string {
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // ISO週: その週の木曜が属する年が「週の年」
  const dow = (t.getDay() + 6) % 7;
  t.setDate(t.getDate() - dow + 3);
  const isoYear = t.getFullYear();
  const firstThursday = new Date(isoYear, 0, 4);
  const fdow = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - fdow + 3);
  const week = 1 + Math.round((t.getTime() - firstThursday.getTime()) / (7 * 86400000));
  return `${isoYear}-W${pad(week)}`;
}

export function weekKeyToStartDate(key: string): Date {
  const [ys, ws] = key.split('-W');
  const isoYear = Number(ys);
  const week = Number(ws);
  const jan4 = new Date(isoYear, 0, 4);
  const week1Monday = startOfWeek(jan4);
  return addDays(week1Monday, (week - 1) * 7);
}

const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

export function formatDateJa(d: Date): string {
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAY_JA[d.getDay()]}）`;
}

export function formatMonthJa(d: Date): string {
  return `${d.getFullYear()}年${d.getMonth() + 1}月`;
}

export function formatTime(iso: IsoLocal): string {
  return iso.slice(11, 16);
}

export function isSameDate(a: Date, b: Date): boolean {
  return toDateKey(a) === toDateKey(b);
}

export function isToday(d: Date): boolean {
  return isSameDate(d, new Date());
}

/** その月の日を全て返す。 */
export function daysOfMonth(monthDate: Date): Date[] {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  return Array.from({ length: last }, (_, i) => new Date(y, m, i + 1));
}

/** カレンダー格子（月曜始まり・6週分）に並べた日の配列を返す。 */
export function calendarGrid(monthDate: Date): Date[] {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** datetime-local 入力用 "YYYY-MM-DDTHH:mm" */
export function toDatetimeLocalValue(iso: IsoLocal): string {
  return iso.slice(0, 16);
}

/** datetime-local の値を IsoLocal へ。オフセットは端末の現在値を使う。 */
export function fromDatetimeLocalValue(v: string): IsoLocal {
  return toIsoLocal(new Date(v));
}
